import { Check, RefreshCw, ShieldCheck, UserRoundCheck, UserRoundX } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type AdminRole, type AdminStatus, type AdminUser } from "../api/client";

const STATUS_LABELS: Record<AdminStatus, string> = {
  pending: "等待啟用",
  active: "已啟用",
  disabled: "已停用"
};

const ACCOUNT_REFRESH_INTERVAL_MS = 10_000;

export function AccountManagementPage({ currentUser }: { currentUser: AdminUser }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSubject, setSavingSubject] = useState("");
  const [error, setError] = useState("");

  async function loadUsers(showLoading = true) {
    if (showLoading) setLoading(true);
    setError("");
    try {
      setUsers((await api.users()).items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  useEffect(() => {
    void loadUsers();

    const refresh = () => void loadUsers(false);
    let timer: number | undefined;
    const stopRefreshing = () => {
      if (timer === undefined) return;
      window.clearInterval(timer);
      timer = undefined;
    };
    const startRefreshing = () => {
      if (timer !== undefined || document.visibilityState !== "visible") return;
      timer = window.setInterval(refresh, ACCOUNT_REFRESH_INTERVAL_MS);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
        startRefreshing();
      } else {
        stopRefreshing();
      }
    };

    startRefreshing();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopRefreshing();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  async function updateUser(user: AdminUser, changes: { status: AdminStatus; role: AdminRole }) {
    setSavingSubject(user.googleSubject);
    setError("");
    try {
      const result = await api.updateUser(user.googleSubject, changes);
      setUsers((items) => items.map((item) => (item.googleSubject === result.user.googleSubject ? result.user : item)));
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setSavingSubject("");
    }
  }

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <div>
          <h2>帳號管理</h2>
          <span className="toolbar-note">新 Google 帳號預設等待啟用</span>
        </div>
        <button className="icon-text-action" type="button" onClick={() => void loadUsers()} disabled={loading}>
          <RefreshCw size={16} aria-hidden="true" />
          <span>重新整理</span>
        </button>
      </div>
      <section className="account-summary" aria-label="帳號摘要">
        <div><strong>{users.length}</strong><span>全部帳號</span></div>
        <div><strong>{users.filter((user) => user.status === "pending").length}</strong><span>等待啟用</span></div>
        <div><strong>{users.filter((user) => user.status === "active").length}</strong><span>已啟用</span></div>
      </section>
      {error ? <div className="error-state">{error}</div> : null}
      <section className="panel account-table-panel">
        <div className="table-wrap">
          <table className="account-table">
            <thead>
              <tr>
                <th>Google 帳號</th>
                <th>狀態</th>
                <th>權限</th>
                <th>最近登入</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = user.googleSubject === currentUser.googleSubject;
                const saving = savingSubject === user.googleSubject;
                return (
                  <tr key={user.googleSubject}>
                    <td>
                      <div className="account-identity">
                        {user.pictureUrl ? <img src={user.pictureUrl} alt="" referrerPolicy="no-referrer" /> : <span>{user.email.slice(0, 1).toUpperCase()}</span>}
                        <div>
                          <strong>{user.displayName || user.email}</strong>
                          <small>{user.email}{isSelf ? " · 目前帳號" : user.protected ? " · 預設管理員" : ""}</small>
                        </div>
                      </div>
                    </td>
                    <td><span className={`account-status ${user.status}`}>{STATUS_LABELS[user.status]}</span></td>
                    <td>
                      <select
                        aria-label={`${user.email} 權限`}
                        value={user.role}
                        disabled={saving || isSelf || user.protected}
                        onChange={(event) => void updateUser(user, { status: user.status, role: event.target.value as AdminRole })}
                      >
                        <option value="viewer">一般使用者</option>
                        <option value="admin">管理員</option>
                      </select>
                    </td>
                    <td>{new Date(`${user.lastLoginAt.replace(" ", "T")}Z`).toLocaleString("zh-HK", { hour12: false })}</td>
                    <td>
                      {user.status === "active" ? (
                        <button
                          className="account-action danger"
                          type="button"
                          disabled={saving || isSelf || user.protected}
                          onClick={() => void updateUser(user, { status: "disabled", role: user.role })}
                        >
                          <UserRoundX size={15} aria-hidden="true" /><span>停用</span>
                        </button>
                      ) : (
                        <button
                          className="account-action approve"
                          type="button"
                          disabled={saving}
                          onClick={() => void updateUser(user, { status: "active", role: user.role })}
                        >
                          {saving ? <RefreshCw size={15} aria-hidden="true" /> : user.status === "pending" ? <UserRoundCheck size={15} aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}
                          <span>啟用</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && users.length === 0 ? <tr><td colSpan={5} className="empty-state">尚無帳號</td></tr> : null}
            </tbody>
          </table>
        </div>
        {loading ? <div className="account-loading"><ShieldCheck size={18} aria-hidden="true" />正在載入帳號</div> : null}
      </section>
    </div>
  );
}
